import type { IProviderService } from "~~/types/case";

export interface ServiceComparisonResult {
  matchScore: number;
  matchingLabels: string[];
  missingLabels: string[];
  badges: string[];
  hasAllServices: boolean;
  hasAnyService: boolean;
}

/**
 * Compare provider services against required services and return comprehensive match information.
 * Handles the complexity of ID-to-value conversion and match calculation in one place.
 *
 * @param providerServiceIds - Array of service IDs from provider.services
 * @param requiredServiceValues - Array of service values from case.choosedService
 * @param allAvailableServices - All provider services from constStore.providerServices
 * @returns Comprehensive service comparison result with scores, labels, and flags
 *
 * @example
 * const result = compareProviderServices(
 *   provider.services,
 *   caseStore.selectedCase?.choosedService || [],
 *   constStore.providerServices
 * );
 * // result.matchScore === 1.0 (100% match)
 * // result.matchingLabels === ["Normal Towing", "Change Tyre"]
 * // result.missingLabels === []
 */
export function compareProviderServices(
  providerServiceIds: string[],
  requiredServiceValues: string[],
  allAvailableServices: IProviderService[]
): ServiceComparisonResult {
  // Convert provider service IDs to values for comparison
  const providerServiceValues = providerServiceIds
    .map((id) => allAvailableServices.find((s) => s.id === id)?.value)
    .filter((value): value is string => value !== undefined);

  // Handle edge case: empty provider services (backward compatibility - means provider supports all services)
  if (providerServiceValues.length === 0) {
    const allActiveLabels = allAvailableServices.filter((s) => s.isActive).map((s) => s.label);

    // If no services required, show all active services
    if (requiredServiceValues.length === 0) {
      return {
        matchScore: 1,
        matchingLabels: allActiveLabels,
        missingLabels: [],
        badges: allActiveLabels,
        hasAllServices: true,
        hasAnyService: true,
      };
    }

    // If services required but provider has none defined, they support everything
    const requiredLabels = requiredServiceValues
      .map((value) => allAvailableServices.find((s) => s.value === value)?.label)
      .filter((label): label is string => label !== undefined);

    return {
      matchScore: 1,
      matchingLabels: requiredLabels,
      missingLabels: [],
      badges: requiredLabels,
      hasAllServices: true,
      hasAnyService: true,
    };
  }

  // Handle edge case: no services required
  if (requiredServiceValues.length === 0) {
    const providerLabels = providerServiceValues
      .map((value) => allAvailableServices.find((s) => s.value === value)?.label)
      .filter((label): label is string => label !== undefined);

    return {
      matchScore: 1,
      matchingLabels: providerLabels,
      missingLabels: [],
      badges: providerLabels,
      hasAllServices: true,
      hasAnyService: providerServiceValues.length > 0,
    };
  }

  // Calculate matching and missing services
  const matchingServiceValues = requiredServiceValues.filter((value) =>
    providerServiceValues.includes(value)
  );

  const missingServiceValues = requiredServiceValues.filter(
    (value) => !providerServiceValues.includes(value)
  );

  // Convert to labels for display
  const matchingLabels = matchingServiceValues
    .map((value) => allAvailableServices.find((s) => s.value === value)?.label)
    .filter((label): label is string => label !== undefined);

  const missingLabels = missingServiceValues
    .map((value) => allAvailableServices.find((s) => s.value === value)?.label)
    .filter((label): label is string => label !== undefined);

  // Calculate match score (percentage of required services that provider offers)
  const matchScore = matchingServiceValues.length / requiredServiceValues.length;

  return {
    matchScore,
    matchingLabels,
    missingLabels,
    badges: matchingLabels.concat(missingLabels),
    hasAllServices: missingServiceValues.length === 0,
    hasAnyService: matchingServiceValues.length > 0,
  };
}

/**
 * Convert service identifiers (IDs or values) to formatted label string.
 * Handles both service IDs and service values transparently.
 *
 * @param serviceIdentifiers - Array of service IDs or values
 * @param allAvailableServices - All provider services from constStore.providerServices
 * @param separator - String to join labels with (default: ", ")
 * @returns Comma-separated string of service labels
 *
 * @example
 * formatServiceLabels(
 *   ["service-uuid-123", "normalTowing"],
 *   constStore.providerServices
 * );
 * // Returns: "Normal Towing, Change Tyre"
 */
export function formatServiceLabels(
  serviceIdentifiers: string[],
  allAvailableServices: IProviderService[],
  separator = ", "
): string {
  if (!serviceIdentifiers || serviceIdentifiers.length === 0) {
    return "";
  }

  const labels = serviceIdentifiers
    .map((identifier) => {
      // Try to find by ID or value
      const service = allAvailableServices.find(
        (s) => s.id === identifier || s.value === identifier
      );
      return service?.label;
    })
    .filter((label): label is string => label !== undefined);

  return labels.join(separator);
}
